import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {IncludedInValuationUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    IncludedInValuation,
    IncludedInValuationOnServerCreated,
    selectIncludedInValuationsActionLoading,
    selectIncludedInValuationById,
    selectLastCreatedIncludedInValuationId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-included-in-valuation-edit',
    templateUrl: './included-in-valuation-edit.component.html',
    styleUrls: ['./included-in-valuation-edit.component.scss'],
})


export class IncludedInValuationEditComponent implements OnInit, OnDestroy {

    model: IncludedInValuation;
    oldModel: IncludedInValuation;

    includedInValuationForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectIncludedInValuationsActionLoading));
        const routeSubscription = this.activatedRoute.params.subscribe(params => {
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectIncludedInValuationById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initIncludedInValuation();
                    }
                });
            } else {
                this.model = new IncludedInValuation();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initIncludedInValuation();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Extent of Investigationss', page: `../admin-management/linked-tables/included-in-valuations`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/included-in-valuations/edit/${id}`
                    : `../admin-management/linked-tables/included-in-valuations/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initIncludedInValuation() {
        this.createForm();
    }


    createForm() {
        this.includedInValuationForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('EXTENT_OF_INVESTIGATION.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('EXTENT_OF_INVESTIGATION.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.includedInValuationForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.includedInValuationForm.markAsPristine();
        this.includedInValuationForm.markAsUntouched();
        this.includedInValuationForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.includedInValuationForm.controls;
        /** check form */
        if (this.includedInValuationForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateIncludedInValuation(this.model);
        } else {
            this.createIncludedInValuation(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: IncludedInValuationModel
     */
    updateIncludedInValuation(_model: IncludedInValuation) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateIncludedInValuation: Update<IncludedInValuation> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new IncludedInValuationUpdated({
            partialItem: updateIncludedInValuation,
            item: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: IncludedInValuationModel
     */
    createIncludedInValuation(_model: IncludedInValuation) {
        this.store.dispatch(new IncludedInValuationOnServerCreated({item: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedIncludedInValuationId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
